import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { NgStyle, NgClass } from '@angular/common';
import { NzSpaceCompactItemDirective } from 'ng-zorro-antd/space';
import { NzSelectComponent, NzOptionComponent, NzOptionGroupComponent } from 'ng-zorro-antd/select';
import { FormsModule } from '@angular/forms';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { ɵNzTransitionPatchDirective } from 'ng-zorro-antd/core/transition-patch';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';
import { NzEmptyComponent } from 'ng-zorro-antd/empty';
import { TranslatePipe } from '@ngx-translate/core';

export interface FiltersConfig {
  filterTitle?: string;
  width?: string;
  icon?: string;
  queryName: string;
  subCode?: string;
  value?: string;
  selectList: any[];
  inputType: string;
  selectSize?: 'large' | 'default' | 'small';
  multiSelect?: boolean;
  translated?: boolean;
  property?: string;
  maxTags?: number;
  minTags?: number;
  defaultValue?: any;
  radioValue?: string;
  selectedItems?: any[];
  order?: number;
}

export enum FilterInputs {
  multiSelect = 'multi-select',
  singleSelect = 'single-select',
  dropdown = 'dropdown',
  groupedDropdown = 'group-dropdown',
  multiGroupedDropdown = 'multi-group-dropdown',
}

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    NgStyle,
    NzSpaceCompactItemDirective,
    NzSelectComponent,
    FormsModule,
    NzOptionComponent,
    NzButtonComponent,
    NzWaveDirective,
    ɵNzTransitionPatchDirective,
    NgClass,
    NzOptionGroupComponent,
    NzCheckboxComponent,
    NzEmptyComponent,
    TranslatePipe,
  ],
})
export class FilterComponent implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);

  @Output() filterChange: EventEmitter<any> = new EventEmitter();
  @Output() filterClearEmit: EventEmitter<string> = new EventEmitter();
  @Input() filterConfig: FiltersConfig;
  @Input() clearFilter = false;
  outputType = '';
  selectedOutputType = '';
  previousOutputType = '';
  selectedOptions = [];
  previousSelectedOptions = [];
  selectedItemsList = [];
  selectedItemsCount = 0;
  checkAll = false;
  changeGrouped = false;

  ngOnInit(): void {
    if (this.filterConfig.selectList.length > 0) {
      if (this.filterConfig.defaultValue) {
        this.filterConfig.property! = this.filterConfig.property! ? this.filterConfig.property! : 'id';
        if (typeof this.filterConfig.defaultValue === 'string') {
          const initialValue = this.filterConfig.selectList.filter(
            (item) => item[this.filterConfig.property!] === this.filterConfig.defaultValue,
          )[0];

          initialValue.selected = true;
          if (this.filterConfig.inputType === 'single-select') {
            this.selectedOptions = initialValue[this.filterConfig.property!];
            this.sendFilterChange(initialValue[this.filterConfig.property!]);
          }
        } else {
          this.filterConfig.selectList[0].selected = true;
          if (this.filterConfig.inputType === 'single-select') {
            this.selectedOptions = this.filterConfig.selectList[0][this.filterConfig.property!];
            this.sendFilterChange(this.filterConfig.selectList[0][this.filterConfig.property!]);
          } else if (this.filterConfig.inputType === FilterInputs.multiGroupedDropdown) {
            this.checkAll = true;
            this.selectedOptions = ['all'];
            this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
              item.selected = true;
              item.properties = item.properties.map((prop) => {
                this.selectedOptions.push(prop.id);
                prop.selected = true;
                return prop;
              });
              return item;
            });
            this.updateSelectedItemsList();
            this.sendFilterChange(this.selectedOptions);
          } else {
            this.selectedOptions.push(this.filterConfig.selectList[0][this.filterConfig.property!]);
            this.sendFilterChange(this.filterConfig.selectList[0][this.filterConfig.property!]);
          }
        }
        this.updateSelectedItemsList();
      } else if (this.filterConfig.selectedItems!.length > 0) {
        if (this.filterConfig.selectList.length === this.filterConfig.selectedItems!.length) {
          this.selectAll(true);
          this.updateSelectedItemsList();
        } else {
          this.selectedOptions = this.filterConfig.selectedItems;
          this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
            const getSelectedItem = this.filterConfig.selectedItems!.filter((ele) => ele === item.id)[0];
            item.selected = !!getSelectedItem;
            return item;
          });
          this.updateSelectedItemsList();
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.clearFilter && changes.clearFilter.currentValue) {
      if (this.filterConfig.selectList.length > 0) {
        if (this.filterConfig.defaultValue) {
          this.filterConfig.property! = this.filterConfig.property! ? this.filterConfig.property! : 'id';
          if (typeof this.filterConfig.defaultValue === 'boolean') {
            const initialValue = this.filterConfig.selectList.filter(
              (item) => item[this.filterConfig.property!] === this.filterConfig.defaultValue,
            )[0];

            initialValue.selected = true;
            if (this.filterConfig.inputType === 'single-select') {
              this.selectedOptions = initialValue[this.filterConfig.property!];
            }
          } else {
            switch (this.filterConfig.inputType) {
              case FilterInputs.dropdown:
                if (!this.filterConfig.multiSelect) {
                  const getDefault = this.filterConfig.selectList.filter(
                    (item) => item[this.filterConfig.property!] === this.filterConfig.defaultValue,
                  )[0];
                  getDefault.selected = true;
                  this.selectedOptions = this.filterConfig.multiSelect
                    ? [getDefault[this.filterConfig.property!]]
                    : getDefault[this.filterConfig.property!];
                }
                break;

              case FilterInputs.groupedDropdown:
                let getParent;
                this.filterConfig.selectList.forEach((item) => {
                  item.properties.forEach((element) => {
                    if (element.id === this.filterConfig.defaultValue) {
                      getParent = item;
                    }
                  });
                });
                this.selectGroup(true, this.filterConfig.defaultValue, 'option', getParent);
                break;
              default:
                const getDefaultValue = this.filterConfig.selectList.filter(
                  (item) => item[this.filterConfig.property!] === this.filterConfig.defaultValue,
                )[0];
                getDefaultValue.selected = true;
                this.selectedOptions = this.filterConfig.multiSelect
                  ? [getDefaultValue[this.filterConfig.property!]]
                  : getDefaultValue[this.filterConfig.property!];
                break;
            }
          }
          this.updateSelectedItemsList();
        } else {
          this.selectedOptions = this.filterConfig.inputType === FilterInputs.groupedDropdown ? null : [];
          this.updateSelectedItemsList();
        }
      } else {
        this.selectedOptions = this.filterConfig.inputType === FilterInputs.groupedDropdown ? null : [];
        this.updateSelectedItemsList();
      }
    }
  }

  initSelect(): void {
    this.filterConfig.property! = this.filterConfig.property! ? this.filterConfig.property! : 'id';
    if (this.filterConfig.selectList.length) {
      let firstElm;
      if (typeof this.filterConfig.defaultValue === 'boolean') {
        firstElm = this.filterConfig.selectList[0];
      } else {
        firstElm = this.filterConfig.selectList.filter((item) => item.id === this.filterConfig.defaultValue)[0];
      }

      if (this.filterConfig.multiSelect) {
        firstElm.selected = true;
        this.selectedOptions.push(firstElm[this.filterConfig.property!]);
      } else {
        this.selectedOptions = firstElm[this.filterConfig.property!];
      }
      this.updateSelectedItemsList();
    }
  }

  changeSelect(value: string[] | string): void {
    if (this.filterConfig.multiSelect && value instanceof Array) {
      if (value.length && value.indexOf('all') === -1 && !this.checkAll) {
        this.filterConfig.selectList.map((product) => {
          product.selected = value.some((val) => val === product[this.filterConfig.property!]);
        });
      } else if (value.length && value.indexOf('all') > -1 && !this.checkAll) {
        this.selectAll(true);
      } else if (value.length && value.indexOf('all') > -1 && this.checkAll) {
        this.filterConfig.selectList.map((product) => {
          product.selected = value.some((val) => val === product[this.filterConfig.property!]);
        });

        this.filterConfig.selectList.map((item) => {
          if (!item.selected) {
            this.removeSelection(item[this.filterConfig.property!]);
          }
        });
      } else {
        this.selectAll(false);
      }
    } else {
      this.changeGrouped = true;
      this.filterConfig.selectList.filter(
        (product) => (product.selected = value === product[this.filterConfig.property!]),
      );
    }

    this.updateSelectedItemsList();
    this.sendFilterChange(value);
  }

  onClearSelect(name: string) {
    this.filterClearEmit.emit(name);
  }

  checkIfAllSelected(val: any[]): boolean {
    let state = false;
    const checkedItemsLength = val.filter((item) => item !== 'all').length;

    state = checkedItemsLength === this.filterConfig.selectList.length;

    return state;
  }

  selectOption(state, selection): void {
    if (this.filterConfig.multiSelect && this.selectedOptions instanceof Array) {
      if (state) {
        this.selectedOptions = [...this.selectedOptions, selection];
        if (this.selectedOptions.length === this.filterConfig.selectList.length) {
          this.selectOption(true, 'all');
        }
      } else {
        this.checkAll = false;
        if (selection === 'all') {
          this.selectedOptions = [];
        } else {
          this.selectedOptions = this.selectedOptions.filter((val) => val !== selection);
          this.selectedOptions = this.selectedOptions.filter((val) => val !== 'all');
        }
      }
    } else {
      if (state) {
        this.selectedOptions = selection;
      } else {
        this.selectedOptions = [];
      }
    }
    this.changeSelect(this.selectedOptions);
  }

  onGroupChange(selection): void {
    this.checkAll = selection.findIndex((item) => item === 'all') > -1;
    const getSelectionFalse = this.selectedOptions.filter((x) => !selection.includes(x));
    const getSelectionTrue = selection.filter((x) => !this.selectedOptions.includes(x));

    if (getSelectionFalse.length > 0) {
      if (getSelectionFalse[0] === 'all') {
        this.selectGroup(false, 'all', 'all');
      } else {
        this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
          item.properties = item.properties.map((prop) => {
            if (prop.id === getSelectionFalse[0]) {
              prop.selected = false;
              item.selected = false;
            }
            return prop;
          });
          return item;
        });
        this.checkAll = false;
        this.selectedOptions = selection.filter((item) => item !== 'all');
        this.sendFilterChange(this.selectedOptions);
      }
    } else if (getSelectionTrue.length > 0) {
      if (getSelectionTrue[0] === 'all') {
        this.selectGroup(true, 'all', 'all');
      } else {
        this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
          item.properties = item.properties.map((prop) => {
            if (prop.id === getSelectionTrue[0]) {
              prop.selected = true;
            }
            return prop;
          });
          const propertiesSelection = item.properties.filter((country) => country.selected);
          const getParent = this.filterConfig.selectList.filter((country) => country.id === item.id)[0];
          if (propertiesSelection.length === getParent.properties.length) {
            getParent.selected = true;
          } else {
            getParent.selected = false;
          }
          const regionSelection = this.filterConfig.selectList.filter((region) => region.selected);
          if (regionSelection.length === this.filterConfig.selectList.length) {
            this.checkAll = true;
          } else {
            this.checkAll = false;
          }
          return item;
        });
        this.selectedOptions = this.checkAll ? ['all', ...selection] : selection;
        this.sendFilterChange(this.selectedOptions);
      }
    }
    this.selectedItemsList = this.selectedOptions.filter((item) => item !== 'all');
    this.selectedItemsCount = this.selectedItemsList.length;
  }

  selectGroup(state, selection, type, parent?): void {
    let selectedGroup;
    switch (type) {
      case 'all':
        this.selectedOptions = [];
        this.checkAll = state;
        if (state) {
          this.selectedOptions = ['all'];
        }
        this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
          item.selected = state;
          item.properties = item.properties.map((prop) => {
            if (state) {
              this.selectedOptions.push(prop.id);
            }
            prop.selected = state;
            return prop;
          });
          return item;
        });
        break;
      case 'group':
        this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
          if (item.id === selection) {
            item.selected = state;
            const regionSelection = this.filterConfig.selectList.filter((region) => region.selected);
            if (regionSelection.length === this.filterConfig.selectList.length) {
              this.checkAll = true;
            } else {
              this.checkAll = false;
            }
            item.properties = item.properties.map((prop) => {
              prop.selected = state;
              if (state) {
                const selectionChanged = this.checkAll
                  ? ['all', ...this.selectedOptions, prop.id]
                  : [...this.selectedOptions, prop.id];
                this.onGroupChange(selectionChanged);
              } else {
                this.selectedOptions = this.selectedOptions.filter((opt) => opt !== prop.id);
                this.checkAll = false;
                this.selectedOptions = this.selectedOptions.filter((opt) => opt !== 'all');
              }
              return prop;
            });
          }
          return item;
        });

        break;
      case 'option':
        selectedGroup = parent.properties.filter((item) => item.id === selection)[0];
        if (selectedGroup) {
          selectedGroup.selected = state;
          if (state) {
            if (this.filterConfig.inputType === FilterInputs.groupedDropdown) {
              this.selectedOptions = selectedGroup.id;
            } else {
              this.selectedOptions.push(selectedGroup.id);
            }
          } else {
            this.selectedOptions = this.selectedOptions.filter((item) => item !== selectedGroup.id);
            this.checkAll = false;
            this.selectedOptions = this.selectedOptions.filter((item) => item !== 'all');
          }
        }
        const propertiesSelection = parent.properties.filter((item) => item.selected);
        const getParent = this.filterConfig.selectList.filter((item) => item.id === parent.id)[0];
        if (propertiesSelection.length === getParent.properties.length) {
          getParent.selected = true;
        } else {
          getParent.selected = false;
        }
        break;
      default:
        break;
    }

    if (this.filterConfig.inputType === FilterInputs.multiGroupedDropdown) {
      this.selectedItemsList = this.selectedOptions.filter((item) => item !== 'all');

      this.selectedItemsCount = this.selectedItemsList.length;
    }
    this.sendFilterChange(this.selectedOptions);
  }

  selectAll(event, direct = false): void {
    this.checkAll = event;
    this.toggleAllChecks(event);
    this.toggleAllOptions(event);
    if (direct) {
      this.updateSelectedItemsList();
    }
  }

  toggleAllChecks(state: boolean): void {
    if (typeof this.filterConfig.selectList[0] !== 'string') {
      this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
        item.selected = state;
        return item;
      });
    }
    if (this.filterConfig.minTags === 1) {
      this.initSelect();
    }
  }

  toggleAllOptions(state): void {
    if (state) {
      this.selectedOptions = this.filterConfig.selectList.map((item) => item[this.filterConfig.property!]);
      this.selectedOptions.push('all');
    } else {
      this.selectedOptions = [];

      if (this.filterConfig.minTags === 1) {
        this.initSelect();
      }
    }
  }

  resetAllCheckboxes(): void {
    if (this.filterConfig.selectList && this.filterConfig.inputType === 'select') {
      this.filterConfig.selectList.map((item) => (item.selected = false));
      if (this.filterConfig.minTags) {
        this.initSelect();
      }
    }
  }

  sendFilterChange(value): void {
    const val = {};

    if (
      this.filterConfig.inputType === 'dropdown' ||
      this.filterConfig.inputType === 'group-dropdown' ||
      this.filterConfig.inputType === 'multi-group-dropdown'
    ) {
      if (this.selectedOptions instanceof Array) {
        val[this.filterConfig.queryName] = this.selectedOptions.filter((item) => item !== 'all');
      } else {
        val[this.filterConfig.queryName] = this.selectedOptions;
      }
    } else {
      val[this.filterConfig.queryName] = value;
    }

    this.filterChange.emit(val);
  }

  removeSelection(item): void {
    if (this.filterConfig.minTags === 1 && this.selectedOptions && this.selectedOptions.length === 1) {
      return;
    }
    if (this.selectedOptions instanceof Array) {
      this.selectedOptions = this.selectedOptions.filter((prod) => prod !== item);
    } else {
      this.selectedOptions = [];
    }
    this.selectOption(false, item);
  }

  updateSelectedItemsList(): void {
    this.selectedItemsList = [];

    if (this.filterConfig.selectList.length && this.selectedOptions) {
      this.filterConfig.selectList.forEach((item: any) => {
        if (
          this.filterConfig.multiSelect &&
          this.selectedOptions.some((prod) => item[this.filterConfig.property!] === prod)
        ) {
          this.selectedItemsList.push(item);
        } else if (!this.filterConfig.multiSelect && !(this.selectedOptions instanceof Array)) {
          if (item[this.filterConfig.property!] === this.selectedOptions) {
            this.selectedItemsList.push(item);
          }
        } else {
          if (item === this.selectedOptions) {
            this.selectedItemsList.push(item);
          }
        }
      });
    }

    this.selectedItemsCount = this.selectedItemsList.filter((item) => item !== 'all').length;
  }

  changeRadio(selection: any): void {
    this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
      item.selected = false;
      return item;
    });
    const selectedValue = this.filterConfig.selectList.filter(
      (item) => item[this.filterConfig.property!] === selection,
    )[0];
    selectedValue.selected = !selectedValue.selected;
    this.sendFilterChange(selection);
  }

  changeChoice(value: string): void {
    if (value.includes('ALL')) {
      this.changeAllChoices();
    } else {
      this.checkAll = false;
      const selectedValue = this.filterConfig.selectList.filter(
        (item) => item[this.filterConfig.property!] === value,
      )[0];
      const selectedAll: any = this.filterConfig.selectList.filter((item) => item.code.includes('ALL'))[0];
      if (selectedValue) {
        if (selectedAll) {
          selectedAll.selected = false;
        }
        selectedValue.selected = !selectedValue.selected;
      }
      this.sendFilterChange(value);
    }
  }

  changeAllChoices(): void {
    this.checkAll = !this.checkAll;
    this.filterConfig.selectList = this.filterConfig.selectList.map((item) => {
      item.selected = !!item[this.filterConfig.property!].includes('ALL');
      return item;
    });
    const selectedData = this.filterConfig.selectList.map((item) => item.selected);
    this.sendFilterChange(selectedData);
  }
}
